import { ArrowRight, Download, Sparkles, Upload } from 'lucide-react';
import type { WorkflowProofContent } from './landingProofContent';

interface LandingWorkflowProofProps {
  content: WorkflowProofContent;
  onGetStarted: () => void;
}

export function LandingWorkflowProof({
  content,
  onGetStarted,
}: LandingWorkflowProofProps) {
  return (
    <section className="relative overflow-hidden border-t border-stone bg-white px-6 py-24 md:py-32">
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-alabaster/70 to-transparent" />
      <div className="relative mx-auto max-w-7xl">
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center rounded-full border border-stone bg-alabaster px-4 py-2 text-xs font-mono uppercase tracking-[0.28em] text-sand shadow-soft">
            {content.eyebrow}
          </div>
          <h2 className="text-4xl font-serif font-light tracking-tight text-charcoal md:text-6xl">
            {content.title}
          </h2>
          <p className="mt-5 text-lg font-light leading-relaxed text-sand md:text-xl">
            {content.description}
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <article className="rounded-[2rem] border border-stone bg-alabaster/60 p-5 shadow-soft">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-olive shadow-soft">
                <Upload className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.24em] text-sand">
                  01 Upload
                </div>
                <div className="text-xl font-serif font-light text-charcoal">
                  Start with the original
                </div>
              </div>
            </div>
            <div className="overflow-hidden rounded-[1.5rem] border border-stone bg-white">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={content.uploadImage}
                  alt="Original room upload"
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {content.uploadTags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-stone bg-white px-3 py-1 text-xs font-mono uppercase tracking-[0.18em] text-sand"
                >
                  {tag}
                </span>
              ))}
            </div>
          </article>

          <article className="rounded-[2rem] border border-stone bg-charcoal p-5 text-alabaster shadow-soft-xl">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-terracotta">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.24em] text-sand">
                  02 Choose
                </div>
                <div className="text-xl font-serif font-light">
                  Dial in the buyer mood
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {content.selectorOptions.map((option) => (
                <div
                  key={option.name}
                  className={`overflow-hidden rounded-[1.4rem] border transition-all duration-300 ${
                    option.selected
                      ? 'border-olive bg-white/10 shadow-soft'
                      : 'border-white/10 bg-white/5'
                  }`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={option.image}
                      alt={`${option.name} style preview`}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="flex items-center justify-between px-3 py-3 text-sm font-light">
                    <span>{option.name}</span>
                    <span className="text-xs font-mono uppercase tracking-[0.18em] text-sand">
                      {option.selected ? 'Selected' : 'Option'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </article>

          <article className="rounded-[2rem] border border-stone bg-alabaster/60 p-5 shadow-soft">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-olive shadow-soft">
                <Download className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.24em] text-sand">
                  03 Export
                </div>
                <div className="text-xl font-serif font-light text-charcoal">
                  Ship the finished asset
                </div>
              </div>
            </div>
            <div className="overflow-hidden rounded-[1.5rem] border border-stone bg-white">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={content.finalImage}
                  alt="Final MLS-ready staged room"
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {content.exportTags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-olive px-3 py-1 text-xs font-mono uppercase tracking-[0.18em] text-alabaster"
                >
                  {tag}
                </span>
              ))}
            </div>
          </article>
        </div>

        <div className="mt-10 text-center">
          <button
            onClick={onGetStarted}
            className="inline-flex items-center gap-2 rounded-full bg-charcoal px-8 py-4 text-base font-medium text-alabaster transition-all duration-500 hover:-translate-y-0.5 hover:bg-olive hover:shadow-lift"
          >
            Build your first listing hero
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
